import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import TicketTagInput from "../../TicketSectionComponents/CreateTicket/TicketTagInput";
import { DROPDOWN_HEIGHT, DROPDOWN_WIDTH } from "./customersConfig";
import CustomerAssignGroupSelect from "./CustomerAssignGroupSelect";

export default function CustomerBulkBar({
  count,
  onApply,
  onReset,
  onDelete,
  showDelete = true,
  groups = [],
  optionsLoading = false,
}) {
  const [assignGroup, setAssignGroup] = useState("");
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (!count) {
      setAssignGroup("");
      setTags([]);
    }
  }, [count]);

  if (!count) return null;

  const hasChanges = Boolean(assignGroup || tags.length);

  const handleApply = () => {
    onApply({
      assignGroup: assignGroup || "",
      tags,
    });
    setAssignGroup("");
    setTags([]);
  };

  const handleReset = () => {
    setAssignGroup("");
    setTags([]);
    onReset();
  };

  return (
    <Box
      sx={{
        pt: 2,
        pb: 2,
        ml: "24px",
        mr: "24px",
        px: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "#F7F8F9",
        borderRadius: "8px",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap", gap: "8px" }}>
        <Typography sx={{ color: "#405261", fontFamily: "Inter", fontSize: 16, fontWeight: 400 }}>
          {count} selected
        </Typography>

        {optionsLoading ? (
          <>
            <Skeleton variant="rounded" width={136} height={32} sx={{ borderRadius: "8px" }} />
            <Skeleton variant="rounded" width={104} height={32} sx={{ borderRadius: "8px" }} />
          </>
        ) : (
          <>
            <CustomerAssignGroupSelect value={assignGroup} onChange={setAssignGroup} groups={groups} />

            <TicketTagInput
              tags={tags}
              setTags={setTags}
              showHeader={false}
              showSelectionTick
              singleLineEllipsis
              emptyLabel="Add Tag"
              sxHeight={32}
              dropDownTop="calc(100% + 5px)"
              dropdownWidth={DROPDOWN_WIDTH}
              dropdownHeight={DROPDOWN_HEIGHT}
              formControlSx={{
                width: 104,
                minWidth: 104,
                maxWidth: 136,
                flexShrink: 0,
                boxSizing: "border-box",
              }}
              chipsContainerSx={{
                width: "100%",
                height: 32,
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            />
          </>
        )}

        {showDelete ? (
          <Button
            size="small"
            onClick={onDelete}
            sx={{
              minWidth: 80,
              height: 32,
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              background: "#FFF",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#FF4D4F",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 400,
              textTransform: "none",
              px: 2,
              "&:hover": { background: "#FFF", borderColor: "#D5DCE1" },
            }}
          >
            Delete
          </Button>
        ) : null}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleReset}
          sx={{
            width: 80,
            height: 32,
            borderRadius: "8px",
            border: "1px solid #D5DCE1",
            background: "#FFF",
            boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
            color: "#405261",
            fontSize: 14,
            fontWeight: 500,
            textTransform: "none",
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!hasChanges}
          sx={{
            height: 32,
            borderRadius: "8px",
            width: 80,
            background: "#148DFD",
            textTransform: "none",
            boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
            fontSize: 14,
            fontWeight: 500,
            "&:not(.Mui-disabled):hover": { backgroundColor: "#0077CC" },
            "&.Mui-disabled": {
              color: "#93A1B0",
              border: "1px solid #C5CED6",
              bgcolor: "#F9FAFA",
            },
          }}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
}
